import { Button, Col, Form, Input, Radio, Row, Select, Space, Tag } from 'antd'
import React, { useContext, useEffect } from 'react'
import { t } from 'i18next'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import PhoneInput from 'react-phone-number-input/input'
import { ActionButton, INTERVENER_TAGS, ROLES } from '../../Common/Common'
import { put } from '../../Common/api'
import { useNavigate } from 'react-router'
import GlobalContext from '../GlobalContext'
import LoginContext from '../LoginContext'

export default function IntervenerForm ({ form, intervener, setLoading, action, addRequest }) {
  const navigate = useNavigate()
  const { notificationApi, screens } = useContext(GlobalContext)
  const { user } = useContext(LoginContext)
  const tagOptions = INTERVENER_TAGS.map(tag => ({ label: t(`Admin.Intervener.attributes.tags.${tag}`), value: tag }))
  const tagRender = props => {
    const { label, value, closable, onClose } = props
    return (
      <Tag
        color={t(`Admin.Intervener.attributes.tagsColor.${value}`)}
        closable={closable}
        onClose={onClose}
        style={{ marginInlineEnd: 4 }}
        variant='outlined'
      >
        {label}
      </Tag>
    )
  }

  useEffect(() => {
    form.resetFields()
    if (intervener) {
      form.setFieldsValue(intervener)
      form.setFieldValue('position', intervener.internal ? 'internal' : 'external')
    }
  }, [intervener])

  const request = async (values) => {
    await put({
      url: `admin/interveners/${values.id}`,
      params: values,
      notification: { api: notificationApi, state: 'update' },
      onSuccess: () => {
        setLoading(false)
      },
      onFailure: (errors) => {
        setLoading(false)
        form.setFields(
          Object.keys(errors).map((key) => ({
            name: key,
            errors: errors[key]
          }))
        )
      }
    })
  }

  const onFinish = (values) => {
    const phones = values.phones?.map((phone) => ({ ...phone, value: phone.value.slice(1) })) || []
    const toDelete = values.fields?.filter(field => !phones.concat(values.emails).map(e => e.id).includes(field.id)) || []
    values.internal = values.position === 'internal'
    values.external = values.position === 'external'
    values.fields_attributes = phones.concat(values.emails).concat(toDelete.map(field => ({ ...field, _destroy: '1' })))
    if (action === 'add') { addRequest(values) } else { request(values) }
  }

  const span = screens.xs ? 24 : 6

  const back = () => {
    navigate('/admin/interveners')
  }

  return (
    <div style={{ position: 'relative' }}>
      <Form
        form={form}
        labelAlign='left'
        onFinish={onFinish}
        disabled={user.roles.includes('reader')}
      >
        <Form.Item name='id' hidden>
          <Input />
        </Form.Item>
        <Form.Item name='fields' hidden>
          <Input />
        </Form.Item>
        <Form.Item name="role" label={t('Admin.Intervener.attributes.role.label')}
          rules={[{ required: true, message: t('rules.required') }]}
        >
          <Radio.Group>
            {ROLES.map((role) => (
              <Radio key={role} value={role}>{t(`Admin.Intervener.attributes.role.${role}`)}</Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Form.Item name="position" label={t('Admin.Intervener.attributes.position')}
          rules={[{ required: true, message: t('rules.required') }]}
        >
          <Radio.Group>
            <Radio key='external' value='external'>{t('Admin.Intervener.attributes.external')}</Radio>
            <Radio key='internal' value='internal'>{t('Admin.Intervener.attributes.internal')}</Radio>
          </Radio.Group>
        </Form.Item>
        <Row gutter={16}>
          <Col>
            <Form.Item name="last_name" label={t('Admin.Intervener.attributes.lastName')}
              rules={[{ required: true, message: t('rules.required') }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item name="first_name" label={t('Admin.Intervener.attributes.firstName')}
              rules={[{ required: true, message: t('rules.required') }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="job" label={t('Admin.Intervener.attributes.job')}>
              <Input placeholder='Responsable nettoyage' />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Form.Item name="description" label={t('Admin.Intervener.attributes.description')} style={{ width: '100%' }} >
            <Input placeholder='Appeler uniquement la semaine / Première personne à contacter' />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item name="tags" label={t('Admin.Intervener.attributes.tags.label')} style={{ width: '100%' }}>
            <Select mode='tags' options={tagOptions} tagRender={tagRender} />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item name="send_automatic_message" label={t('Admin.Intervener.attributes.send_automatic_message')}>
            <Radio.Group>
              <Radio value={true} >{t('Admin.message.yes')}</Radio>
              <Radio value={false} >{t('Admin.message.no')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Row>
        <h3>{t('Admin.IntervenerForm.phones')}</h3>
        <Form.List name="phones">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{
                    display: 'flex',
                    marginBottom: 8
                  }}
                  align="baseline"
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'value']}
                    rules={[{ required: true, message: t('rules.required') }]}
                    label={t('Admin.Intervener.attributes.phone')}
                  >
                    <PhoneInput className='ant-input css-dev-only-do-not-override-xfszwz PhoneInput' international defaultCountry={'FR'}
                      placeholder="06 39 98 80 12" style={{ fontSize: '14px', padding: '7px 11px' }} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'description']}
                    label={t('Admin.Intervener.attributes.description')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, 'id']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, 'field_type']} hidden initialValue={'phone'}>
                    <Input />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {`${t('actions.add')} ${t('Admin.IntervenerForm.aPhone')}`}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        <h3>{t('Admin.IntervenerForm.emails')}</h3>
        <Form.List name="emails">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{
                    display: 'flex',
                    marginBottom: 8
                  }}
                  align="baseline"
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'value']}
                    rules={[{ required: true, message: t('rules.required') }]}
                    label={t('Admin.Intervener.attributes.email')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'description']}
                    label={t('Admin.Intervener.attributes.description')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, 'id']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, 'field_type']} hidden initialValue={'email'}>
                    <Input />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {`${t('actions.add')} ${t('Admin.IntervenerForm.anEmail')}`}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>

      <Row>
        {(action === 'update') && <>
          <Col span={span} style={{ position: 'absolute', right: '5px' }} >
            <Button type="primary" onClick={back} >
              {t('Admin.btn.back')}
            </Button>
          </Col>
          <Col span={span} style={{ paddingBottom: '10px' }} >
            <ActionButton type="primary" htmlType="submit" onClick={form.submit} >
              {t('Admin.btn.submit')}
            </ActionButton>
          </Col>
        </>
        }
      </Row>
    </div>
  )
}
